'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Chevron, Drawer } from '@sprd/sprd-component-kit/v2';
import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useAddItem } from '@/hooks/useCartHooks';
import { useDiscountedPrice } from '@/hooks/useDiscountedPrice';
import type { ProductTypeWithEmbroideryInformation } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/actions/productType';
import { AppearanceSelector } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/AppearanceSelector';
import { SizeSelectorDesktop, SizeSelectorMobile } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/SizeSelectors';
import { pdpParamsWithSyncAtom } from '@/atoms/pdpParamsAtom';
import { useRouter } from '@/i18n/navigation';
import { basketAtom } from '@/store/atoms';
import { WARNING_NO_BLANK_ID } from '@/utility/constants';
import { getDiscountedPrice } from '@/utility/getDiscountedPrice';
import { getAssetUrlByName } from '@/utility/getImageUrl';
import { Assortment } from '@/utility/shopIds';
import { tracker } from '@/utility/tracking/tracker';
import { sanitizeQuery } from '@/utility/urls';
import { CTADivider, CTASectionDesktop } from './CTASectionDesktop';
const IMAGE_SIZE = 128;
export type ActionsSectionProps = {
  productType: ClientProductType<ProductTypeWithEmbroideryInformation>;
  brandId: string | undefined;
  amplienceMediaBaseUrl: string;
  platform: Platform;
  shopId: string;
  currentDiscountAmount: number;
  // the shop the product type is loaded from (`shopId`), for its strike price
  assortment: Assortment;
};
const isSpreadBrand = (brandId: string | undefined) => brandId === 'SPREAD';
export const ActionsSection = t0 => {
  const $ = _c(174);
  const {
    productType,
    brandId,
    amplienceMediaBaseUrl,
    platform,
    shopId,
    assortment,
    currentDiscountAmount
  } = t0;
  const t = useTranslations();
  const router = useRouter();
  const pdpParams = useAtomValue(pdpParamsWithSyncAtom);
  const selectedAppearanceId = pdpParams?.appearanceId ?? productType.defaultValues.defaultAppearance.id;
  const [selectedSizeId, setSelectedSizeId] = useState(undefined);
  const [sizeMissingError, setSizeMissingError] = useState(false);
  const [openDialog, setOpenDialog] = useState();
  let t1;
  if ($[0] !== productType.labels?.warnings) {
    t1 = productType.labels?.warnings?.some(_temp) ?? false;
    $[0] = productType.labels?.warnings;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isBlankVerboten = t1;
  let t2;
  if ($[2] !== brandId || $[3] !== isBlankVerboten) {
    t2 = !isBlankVerboten && isSpreadBrand(brandId);
    $[2] = brandId;
    $[3] = isBlankVerboten;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const showAddToCart = t2;
  const {
    mutate: addToCart
  } = useAddItem("CYO AddToCart");
  const setOpenBasket = useSetAtom(basketAtom);
  let selectedAppearanceStockState;
  let t3;
  if ($[5] !== productType.stockStates || $[6] !== selectedAppearanceId) {
    let t4;
    if ($[9] !== selectedAppearanceId) {
      t4 = stockState => stockState.appearance.id === selectedAppearanceId;
      $[9] = selectedAppearanceId;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    selectedAppearanceStockState = productType.stockStates.filter(t4);
    t3 = selectedAppearanceStockState.every(_temp2);
    $[5] = productType.stockStates;
    $[6] = selectedAppearanceId;
    $[7] = selectedAppearanceStockState;
    $[8] = t3;
  } else {
    selectedAppearanceStockState = $[7];
    t3 = $[8];
  }
  const outOfStock = t3;
  let t4;
  if ($[11] !== currentDiscountAmount || $[12] !== productType.price.vatIncluded) {
    t4 = getDiscountedPrice(productType.price.vatIncluded, currentDiscountAmount);
    $[11] = currentDiscountAmount;
    $[12] = productType.price.vatIncluded;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const priceWithVolumeDiscount = t4;
  const t5 = productType.id;
  const t6 = Boolean(currentDiscountAmount);
  let t7;
  if ($[14] !== assortment || $[15] !== t6) {
    t7 = {
      hasVolumeDiscount: t6,
      assortment
    };
    $[14] = assortment;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const discountedPrice = useDiscountedPrice(priceWithVolumeDiscount, t5, t7);
  let t8;
  let t9;
  if ($[17] !== selectedAppearanceStockState || $[18] !== selectedSizeId) {
    t8 = () => {
      if (!selectedSizeId) {
        return;
      }
      const isSizeAvailable = selectedAppearanceStockState.some(stockState_1 => stockState_1.size.id === selectedSizeId && stockState_1.available);
      if (isSizeAvailable) {
        return;
      }
      setSelectedSizeId(undefined);
    };
    t9 = [selectedAppearanceStockState, selectedSizeId];
    $[17] = selectedAppearanceStockState;
    $[18] = selectedSizeId;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== productType.id || $[22] !== router || $[23] !== selectedAppearanceId) {
    t10 = sizeId => {
      router.push({
        pathname: "/cyo/designer",
        query: sanitizeQuery({
          productType: productType.id,
          appearance: selectedAppearanceId,
          size: sizeId
        })
      });
    };
    $[21] = productType.id;
    $[22] = router;
    $[23] = selectedAppearanceId;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const routeToDesignerPrint = t10;
  let t11;
  if ($[25] !== productType.id || $[26] !== router || $[27] !== selectedAppearanceId) {
    t11 = sizeId_0 => {
      router.push({
        pathname: "/cyo/designer",
        query: sanitizeQuery({
          productType: productType.id,
          appearance: selectedAppearanceId,
          size: sizeId_0,
          embroidery: true
        })
      });
    };
    $[25] = productType.id;
    $[26] = router;
    $[27] = selectedAppearanceId;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const routeToDesignerEmbroidery = t11;
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = sizeId_1 => {
      setSelectedSizeId(sizeId_1);
      setSizeMissingError(false);
      tracker.trackFeature({
        featureUsed: "choose-size",
        detail: sizeId_1
      });
      tracker.trackClientProduct({
        product: {
          sizeId: sizeId_1
        }
      });
    };
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const setTrackedSizeId = t12;
  let t13;
  if ($[30] !== productType.isEmbroideryOnly || $[31] !== productType.isEmbroiderySupported || $[32] !== routeToDesignerEmbroidery || $[33] !== routeToDesignerPrint) {
    t13 = sizeId_2 => {
      if (productType.isEmbroideryOnly) {
        routeToDesignerEmbroidery(sizeId_2);
      } else {
        if (productType.isEmbroiderySupported) {
          setOpenDialog("select-print");
        } else {
          routeToDesignerPrint(sizeId_2);
        }
      }
    };
    $[30] = productType.isEmbroideryOnly;
    $[31] = productType.isEmbroiderySupported;
    $[32] = routeToDesignerEmbroidery;
    $[33] = routeToDesignerPrint;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const handleSizeSelect = t13;
  let t14;
  if ($[35] !== addToCart || $[36] !== discountedPrice || $[37] !== productType.id || $[38] !== productType.name || $[39] !== selectedAppearanceId || $[40] !== setOpenBasket || $[41] !== shopId) {
    t14 = sizeId_3 => {
      addToCart({
        cartItems: [{
          productTypeId: +productType.id,
          shopId: +shopId,
          sizeId: sizeId_3,
          appearanceId: selectedAppearanceId,
          quantity: 1,
          channel: "cyo",
          price: Math.round(discountedPrice * 100) / 100,
          articleName: productType.name,
          editLink: window.location.href
        }]
      }, {
        onSuccess: () => {
          setOpenDialog(undefined);
          setOpenBasket(true);
        }
      });
    };
    $[35] = addToCart;
    $[36] = discountedPrice;
    $[37] = productType.id;
    $[38] = productType.name;
    $[39] = selectedAppearanceId;
    $[40] = setOpenBasket;
    $[41] = shopId;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const handleAddToCart = t14;
  let t15;
  if ($[43] !== productType || $[44] !== selectedAppearanceId) {
    t15 = <AppearanceSelector productType={productType} selectedAppearanceId={selectedAppearanceId} />;
    $[43] = productType;
    $[44] = selectedAppearanceId;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== platform || $[47] !== productType || $[48] !== selectedAppearanceStockState || $[49] !== selectedSizeId || $[50] !== sizeMissingError) {
    t16 = <SizeSelectorDesktop productType={productType} selectedAppearanceStockState={selectedAppearanceStockState} selectedSizeId={selectedSizeId} onSizeSelection={setTrackedSizeId} platform={platform} error={sizeMissingError} />;
    $[46] = platform;
    $[47] = productType;
    $[48] = selectedAppearanceStockState;
    $[49] = selectedSizeId;
    $[50] = sizeMissingError;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setSizeMissingError(true);
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== handleAddToCart || $[54] !== outOfStock || $[55] !== productType || $[56] !== selectedAppearanceId || $[57] !== selectedSizeId || $[58] !== showAddToCart) {
    t18 = <CTASectionDesktop productType={productType} selectedSizeId={selectedSizeId} selectedAppearanceId={selectedAppearanceId} outOfStock={outOfStock} handleAddToCart={handleAddToCart} showAddToCart={showAddToCart} onSizeMissing={t17} />;
    $[53] = handleAddToCart;
    $[54] = outOfStock;
    $[55] = productType;
    $[56] = selectedAppearanceId;
    $[57] = selectedSizeId;
    $[58] = showAddToCart;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t15 || $[61] !== t16 || $[62] !== t18) {
    t19 = <div className="hidden flex-col gap-6 md:flex">{t15}{t16}{t18}</div>;
    $[60] = t15;
    $[61] = t16;
    $[62] = t18;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== productType || $[65] !== selectedAppearanceId) {
    t20 = <AppearanceSelector productType={productType} selectedAppearanceId={selectedAppearanceId} />;
    $[64] = productType;
    $[65] = selectedAppearanceId;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== handleSizeSelect || $[68] !== outOfStock || $[69] !== productType.sizes[0] || $[70] !== productType.sizes.length) {
    t21 = () => {
      if (productType.sizes.length === 1 && !outOfStock) {
        const oneSizeId = productType.sizes[0].id;
        setTrackedSizeId(oneSizeId);
        handleSizeSelect(oneSizeId);
      } else {
        setOpenDialog("select-size-create");
      }
    };
    $[67] = handleSizeSelect;
    $[68] = outOfStock;
    $[69] = productType.sizes[0];
    $[70] = productType.sizes.length;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t) {
    t22 = t("cyoAssortmentDetailPage.createNow");
    $[72] = t;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== t21 || $[75] !== t22) {
    t23 = <Button variant="gradient" className="w-full" size="l" onClick={t21}>{t22}</Button>;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  let t24;
  if ($[77] !== handleAddToCart || $[78] !== outOfStock || $[79] !== productType.sizes[0] || $[80] !== productType.sizes.length || $[81] !== showAddToCart || $[82] !== t) {
    t24 = showAddToCart && <div><CTADivider /><Button size="l" className="w-full" disabled={outOfStock} onClick={() => {
        if (productType.sizes.length === 1) {
          const oneSizeId_0 = productType.sizes[0].id;
          setTrackedSizeId(oneSizeId_0);
          handleAddToCart(oneSizeId_0);
        } else {
          setOpenDialog("select-size-cart");
        }
      }}>{t("mpDetailPage.addToCart")}</Button></div>;
    $[77] = handleAddToCart;
    $[78] = outOfStock;
    $[79] = productType.sizes[0];
    $[80] = productType.sizes.length;
    $[81] = showAddToCart;
    $[82] = t;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  let t25;
  if ($[84] !== t23 || $[85] !== t24) {
    t25 = <div className="flex flex-col gap-3">{t23}{t24}</div>;
    $[84] = t23;
    $[85] = t24;
    $[86] = t25;
  } else {
    t25 = $[86];
  }
  const t26 = openDialog === "select-size-create" || openDialog === "select-size-cart";
  const t27 = openDialog === "select-size-create" ? "cyoAssortmentDetailPage.selectSizeAndCreateNow" : "cyoAssortmentDetailPage.selectSize";
  let t28;
  if ($[87] !== t || $[88] !== t27) {
    t28 = t(t27);
    $[87] = t;
    $[88] = t27;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  let t29;
  if ($[90] !== t) {
    t29 = t("general.close");
    $[90] = t;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== openDialog) {
    t30 = open => setOpenDialog(open ? openDialog ?? "select-size-create" : undefined);
    $[92] = openDialog;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== handleAddToCart || $[95] !== handleSizeSelect || $[96] !== openDialog) {
    t31 = sizeId_4 => {
      setTrackedSizeId(sizeId_4);
      if (openDialog === "select-size-cart") {
        handleAddToCart(sizeId_4);
      } else {
        handleSizeSelect(sizeId_4);
      }
    };
    $[94] = handleAddToCart;
    $[95] = handleSizeSelect;
    $[96] = openDialog;
    $[97] = t31;
  } else {
    t31 = $[97];
  }
  let t32;
  if ($[98] !== platform || $[99] !== productType || $[100] !== selectedAppearanceStockState || $[101] !== selectedSizeId || $[102] !== t31) {
    t32 = <div className="flex flex-col gap-3"><SizeSelectorMobile productType={productType} selectedAppearanceStockState={selectedAppearanceStockState} selectedSizeId={selectedSizeId} onSizeSelection={t31} platform={platform} /></div>;
    $[98] = platform;
    $[99] = productType;
    $[100] = selectedAppearanceStockState;
    $[101] = selectedSizeId;
    $[102] = t31;
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  let t33;
  if ($[104] !== t26 || $[105] !== t28 || $[106] !== t29 || $[107] !== t30 || $[108] !== t32) {
    t33 = <Drawer dismissible={true} open={t26} title={t28} closeLabel={t29} onOpenChange={t30} className="pb-12">{t32}</Drawer>;
    $[104] = t26;
    $[105] = t28;
    $[106] = t29;
    $[107] = t30;
    $[108] = t32;
    $[109] = t33;
  } else {
    t33 = $[109];
  }
  const t34 = openDialog === "select-print";
  let t35;
  if ($[110] !== t) {
    t35 = t("cyoAssortmentDetailPage.startCreatingYour");
    $[110] = t;
    $[111] = t35;
  } else {
    t35 = $[111];
  }
  let t36;
  if ($[112] !== t35) {
    t36 = <span className="bg-(image:--gradient-default) bg-clip-text text-transparent">{t35}</span>;
    $[112] = t35;
    $[113] = t36;
  } else {
    t36 = $[113];
  }
  let t37;
  if ($[114] !== t) {
    t37 = t("general.close");
    $[114] = t;
    $[115] = t37;
  } else {
    t37 = $[115];
  }
  let t38;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = open_0 => setOpenDialog(open_0 ? "select-print" : "select-size-create");
    $[116] = t38;
  } else {
    t38 = $[116];
  }
  let t39;
  if ($[117] !== t) {
    t39 = t("cyoAssortmentDetailPage.selectPrintType");
    $[117] = t;
    $[118] = t39;
  } else {
    t39 = $[118];
  }
  let t40;
  if ($[119] !== t39) {
    t40 = <legend className="sr-only">{t39}</legend>;
    $[119] = t39;
    $[120] = t40;
  } else {
    t40 = $[120];
  }
  let t41;
  if ($[121] !== t) {
    t41 = t("cyoAssortmentDetailPage.createNow");
    $[121] = t;
    $[122] = t41;
  } else {
    t41 = $[122];
  }
  let t42;
  if ($[123] !== t) {
    t42 = t("general.customizableProducts");
    $[123] = t;
    $[124] = t42;
  } else {
    t42 = $[124];
  }
  let t43;
  if ($[125] !== amplienceMediaBaseUrl) {
    t43 = getAssetUrlByName("digital-print", amplienceMediaBaseUrl, IMAGE_SIZE);
    $[125] = amplienceMediaBaseUrl;
    $[126] = t43;
  } else {
    t43 = $[126];
  }
  let t44;
  if ($[127] !== t) {
    t44 = t("cyoAssortmentDetailPage.printTypes.print");
    $[127] = t;
    $[128] = t44;
  } else {
    t44 = $[128];
  }
  let t45;
  if ($[129] !== routeToDesignerPrint || $[130] !== selectedSizeId) {
    t45 = () => routeToDesignerPrint(selectedSizeId ?? "");
    $[129] = routeToDesignerPrint;
    $[130] = selectedSizeId;
    $[131] = t45;
  } else {
    t45 = $[131];
  }
  let t46;
  if ($[132] !== t41 || $[133] !== t42 || $[134] !== t43 || $[135] !== t44 || $[136] !== t45) {
    t46 = <li><PrintTypeItem title={t41} text={t42} imgSrc={t43} imgAlt={t44} onClick={t45} /></li>;
    $[132] = t41;
    $[133] = t42;
    $[134] = t43;
    $[135] = t44;
    $[136] = t45;
    $[137] = t46;
  } else {
    t46 = $[137];
  }
  let t47;
  if ($[138] !== t) {
    t47 = t("cyoAssortmentDetailPage.embroiderNow");
    $[138] = t;
    $[139] = t47;
  } else {
    t47 = $[139];
  }
  let t48;
  if ($[140] !== t) {
    t48 = t("general.customizableProducts");
    $[140] = t;
    $[141] = t48;
  } else {
    t48 = $[141];
  }
  let t49;
  if ($[142] !== amplienceMediaBaseUrl) {
    t49 = getAssetUrlByName("embroidery", amplienceMediaBaseUrl, IMAGE_SIZE);
    $[142] = amplienceMediaBaseUrl;
    $[143] = t49;
  } else {
    t49 = $[143];
  }
  let t50;
  if ($[144] !== t) {
    t50 = t("cyoAssortmentDetailPage.printTypes.embroidery");
    $[144] = t;
    $[145] = t50;
  } else {
    t50 = $[145];
  }
  let t51;
  if ($[146] !== routeToDesignerEmbroidery || $[147] !== selectedSizeId) {
    t51 = () => routeToDesignerEmbroidery(selectedSizeId ?? "");
    $[146] = routeToDesignerEmbroidery;
    $[147] = selectedSizeId;
    $[148] = t51;
  } else {
    t51 = $[148];
  }
  let t52;
  if ($[149] !== t47 || $[150] !== t48 || $[151] !== t49 || $[152] !== t50 || $[153] !== t51) {
    t52 = <li><PrintTypeItem title={t47} text={t48} imgSrc={t49} imgAlt={t50} onClick={t51} /></li>;
    $[149] = t47;
    $[150] = t48;
    $[151] = t49;
    $[152] = t50;
    $[153] = t51;
    $[154] = t52;
  } else {
    t52 = $[154];
  }
  let t53;
  if ($[155] !== t46 || $[156] !== t52) {
    t53 = <ul className="flex flex-col gap-2 px-4 pt-4 sm:px-6">{t46}{t52}</ul>;
    $[155] = t46;
    $[156] = t52;
    $[157] = t53;
  } else {
    t53 = $[157];
  }
  let t54;
  if ($[158] !== t40 || $[159] !== t53) {
    t54 = <fieldset>{t40}{t53}</fieldset>;
    $[158] = t40;
    $[159] = t53;
    $[160] = t54;
  } else {
    t54 = $[160];
  }
  let t55;
  if ($[161] !== t34 || $[162] !== t36 || $[163] !== t37 || $[164] !== t54) {
    t55 = <Drawer dismissible={true} open={t34} title={t36} closeLabel={t37} onOpenChange={t38} className="pb-12">{t54}</Drawer>;
    $[161] = t34;
    $[162] = t36;
    $[163] = t37;
    $[164] = t54;
    $[165] = t55;
  } else {
    t55 = $[165];
  }
  let t56;
  if ($[166] !== t20 || $[167] !== t25 || $[168] !== t33 || $[169] !== t55) {
    t56 = <div className="flex flex-col gap-6 md:hidden">{t20}{t25}{t33}{t55}</div>;
    $[166] = t20;
    $[167] = t25;
    $[168] = t33;
    $[169] = t55;
    $[170] = t56;
  } else {
    t56 = $[170];
  }
  let t57;
  if ($[171] !== t19 || $[172] !== t56) {
    t57 = <>{t19}{t56}</>;
    $[171] = t19;
    $[172] = t56;
    $[173] = t57;
  } else {
    t57 = $[173];
  }
  return t57;
};
type PrintTypeItemProps = {
  title: string;
  text: string;
  imgSrc: string;
  imgAlt: string;
  onClick: () => void;
};
const PrintTypeItem = t0 => {
  const $ = _c(17);
  const {
    title,
    text,
    imgSrc,
    imgAlt,
    onClick
  } = t0;
  let t1;
  if ($[0] !== imgAlt || $[1] !== imgSrc) {
    t1 = <img className="h-16 w-16 object-cover" src={imgSrc} alt={imgAlt} />;
    $[0] = imgAlt;
    $[1] = imgSrc;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== title) {
    t2 = <p className="text-left font-made">{title}</p>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== text) {
    t3 = <p className="text-sm">{text}</p>;
    $[5] = text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span>{t2}{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <div className="flex items-center gap-3">{t1}{t4}</div>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Chevron className="w-6 -rotate-90" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onClick || $[15] !== t5) {
    t7 = <button className="inline-flex w-full items-center justify-between" onClick={onClick}>{t5}{t6}</button>;
    $[14] = onClick;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
function _temp(label) {
  return label.id === WARNING_NO_BLANK_ID;
}
function _temp2(stockState_0) {
  return !stockState_0.available;
}